import { ChangeDetectorRef, Component, ErrorHandler, HostListener, Inject, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgClass } from '@angular/common';

import {
    MijnNavigationEventsList,
    MijnRoute,
    MijnRoutes,
    MijnWebcomponentIdentifier,
    MijnWebcomponentProviderName
} from '@ap/mijn-navigatie';
import { WebcomponentEvent } from '@ap/webcomponent';
import { ApContentService } from '@ap/content';
import { MijnErrorHandler, MijnErrorHandlerModule } from '@ap/mijn-error-handler';

import { ChatNotification, MijnOverzichtTemplateModule } from '../../mijn-overzicht-template';
import { ActieTemplateSettings, OverzichtTemplateSettings, TemplateClickEvent, TemplateSettings } from '../mijn-omgeving-template.model';
import { MijnOmgevingTemplateService } from '../mijn-omgeving-template.service';
import content from '../../mijn-template.content.json';
import { MijnActieTemplateModule } from '../../mijn-actie-template';

import { MijnOmgevingWebpartMessagingService } from './webpart-messaging.service';

const defaultEvents: WebcomponentEvent[] = MijnNavigationEventsList.map((eventName) => ({ name: eventName, action: eventName }));
const MIJN_OMGEVING = 'mijn-omgeving';
/**
 * Only to be used for test-wrapper purposes!
 */
@Component({
    selector: MIJN_OMGEVING,
    templateUrl: '../mijn-omgeving-template.component.html',
    imports: [NgClass, MijnOverzichtTemplateModule, MijnActieTemplateModule, RouterModule, MijnErrorHandlerModule],
    providers: [{ provide: ErrorHandler, useClass: MijnErrorHandler }]
})
export class BaseMijnOmgevingWrapperStandaloneComponent implements OnInit {
    public currentTemplate = 'OVERZICHT';
    public templateSettings: TemplateSettings;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly contentService = inject(ApContentService);
    private readonly mijnOmgevingTemplateService = inject(MijnOmgevingTemplateService);
    private readonly router = inject(Router);
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly webpartMessagingService = inject(MijnOmgevingWebpartMessagingService);

    @HostListener('window:dispatch-webpart-message', ['$event'])
    dispatchMessage(event: Event) {
        this.webpartMessagingService.onTransportEvent(event as CustomEvent);
    }

    public constructor(@Inject(MijnWebcomponentProviderName) private readonly webcomponentIdentifier: MijnWebcomponentIdentifier) {
        /* note: unconmment next line for debugging navigation
         * router.events.subscribe((event) => console.log(event));
         */
    }

    public get overzichtTemplateSettings(): OverzichtTemplateSettings {
        return this.templateSettings as OverzichtTemplateSettings;
    }

    public get actieTemplateSettings(): ActieTemplateSettings {
        return this.templateSettings as ActieTemplateSettings;
    }

    public formatTabTitle(tabTitle: string): string {
        return `${tabTitle} - ${this.contentService.get('mijn.omgeving.template.tabTitle.postFix')}`;
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        defaultEvents.forEach((event) => this.addWebcomponentEventListener(MIJN_OMGEVING, event));
        document.querySelector(MIJN_OMGEVING).addEventListener('templateSettings', (settings: CustomEvent) => {
            if (!!settings.detail?.setTemplate) {
                this.currentTemplate = settings.detail.setTemplate;
            }
            this.templateSettings = settings.detail;
        });
        // This fixes the NG0100 changed after check error
        this.changeDetector.detectChanges();
    }

    public primaryActionClicked(): void {
        this.mijnOmgevingTemplateService.sendTemplateClickEvent(
            (this.templateSettings as OverzichtTemplateSettings).webcomponentIdentifier,
            TemplateClickEvent.PRIMARYACTION
        );
    }
    public secondaryActionClicked(): void {
        this.mijnOmgevingTemplateService.sendTemplateClickEvent(
            (this.templateSettings as OverzichtTemplateSettings).webcomponentIdentifier,
            TemplateClickEvent.SECONDARYACTION
        );
    }

    public chatNotificationSent(event: ChatNotification): void {
        this.mijnOmgevingTemplateService.sendTemplateClickEvent(
            (this.templateSettings as OverzichtTemplateSettings).webcomponentIdentifier,
            TemplateClickEvent.CHATNOTIFICATION,
            event
        );
    }

    public closeTemplate(): void {
        this.mijnOmgevingTemplateService.sendTemplateClickEvent(
            (this.templateSettings as ActieTemplateSettings).webcomponentIdentifier,
            TemplateClickEvent.CLOSETEMPLATE
        );
    }

    public addWebcomponentEventListener(tagName: string, customEvent: WebcomponentEvent): void {
        const element = document.querySelector(tagName);
        element.addEventListener(customEvent.name, () => {
            let mijnRoute: MijnRoute;
            MijnRoutes.forEach((route) => {
                if (route.navigationEvent === customEvent.action) {
                    mijnRoute = {
                        ...route
                    };
                }
            });
            if (this.isChildOfCurrent(mijnRoute)) {
                this.router.navigate([`./${mijnRoute.path}`], { relativeTo: this.activatedRoute });
            } else if (this.isParentOfCurrent(mijnRoute)) {
                this.router.navigate([`/${mijnRoute.path}`], { relativeTo: this.activatedRoute });
            } else {
                this.router.navigate([`/${mijnRoute.path}`]);
            }
        });
    }

    private isParentOfCurrent(mijnRoute: MijnRoute) {
        const currentPath = window.location.hash.substring(1);
        return currentPath.indexOf(`/${mijnRoute.path}`) === 0;
    }

    private isChildOfCurrent(mijnRoute: MijnRoute) {
        const currentPath = window.location.hash.substring(1);
        return mijnRoute.regex.test(`${currentPath}/${mijnRoute.path}`);
    }
}
